import { supabase } from "@/integrations/supabase/client";
import type { UserAiProfile } from "@/types/userAiProfile";

/**
 * Fetch all user AI profiles (admin list with user name/email joins).
 */
export async function fetchUserAiProfiles(): Promise<UserAiProfile[]> {
  const { data, error } = await supabase
    .from("user_ai_profiles")
    .select("*")
    .order("updated_at", { ascending: false });

  if (error) throw error;

  // Fetch profile info for the user_ids
  const userIds = [...new Set((data || []).map((r: any) => r.user_id))];
  let profileMap: Record<string, { name: string; email: string }> = {};
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("uid, name, email")
      .in("uid", userIds);
    if (profiles) {
      for (const p of profiles) {
        profileMap[p.uid] = { name: p.name, email: p.email };
      }
    }
  }

  return (data || []).map((row: any) => {
    const profile = profileMap[row.user_id];
    return {
      id: row.id,
      userId: row.user_id,
      detectedProfession: row.detected_profession,
      professionConfidence: row.profession_confidence || 0,
      communicationStyle: row.communication_style,
      languagePreference: row.language_preference,
      readingLevel: row.reading_level,
      topicsOfInterest: row.topics_of_interest || [],
      preferredResponseLength: row.preferred_response_length,
      asksDeepQuestions: row.asks_deep_questions || false,
      locationHint: row.location_hint,
      signalCount: row.signal_count || 0,
      lastUpdatedFromSessionId: row.last_updated_from_session_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      userName: profile?.name,
      userEmail: profile?.email,
    } as UserAiProfile;
  });
}

/**
 * Fetch a single user AI profile by ID.
 */
export async function fetchUserAiProfile(id: number): Promise<UserAiProfile> {
  const { data, error } = await supabase
    .from("user_ai_profiles")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;

  // Fetch profile info
  let userName: string | undefined;
  let userEmail: string | undefined;
  if (data.user_id) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("name, email")
      .eq("uid", data.user_id)
      .single();
    userName = profile?.name;
    userEmail = profile?.email;
  }

  return {
    id: data.id,
    userId: data.user_id,
    detectedProfession: data.detected_profession,
    professionConfidence: data.profession_confidence || 0,
    communicationStyle: data.communication_style,
    languagePreference: data.language_preference,
    readingLevel: data.reading_level,
    topicsOfInterest: data.topics_of_interest || [],
    preferredResponseLength: data.preferred_response_length,
    asksDeepQuestions: data.asks_deep_questions || false,
    locationHint: data.location_hint,
    signalCount: data.signal_count || 0,
    lastUpdatedFromSessionId: data.last_updated_from_session_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    userName,
    userEmail,
  } as UserAiProfile;
}
